/**
 * theme.ts — light/dark theme controller (T-156, BEAD-0063)
 *
 * The design tokens for both themes already live in index.css (:root = light, .dark = dark),
 * bridged to Tailwind utilities via `@theme inline`, and index.css declares
 * `@custom-variant dark (&:where(.dark, .dark *))` so the `dark:` utilities follow the
 * `.dark` class (not the raw media query) — which is what lets a MANUAL override work.
 *
 * This module owns the *choice*:
 *   - 'system' (default): follow prefers-color-scheme, and live-update while the app is open.
 *   - 'light' / 'dark': an explicit manual override, persisted, that wins over the OS pref
 *     until the user clears it (picks 'system' again).
 *
 * No-flash: index.html runs the same resolution inline before first paint, so the correct
 * class is on <html> before React mounts. This module keeps it in sync afterwards.
 */
export type ThemeChoice = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'optiyield_theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

/** The persisted manual choice, or 'system' when there is no override (default). */
export function getStoredChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY)
    if (v === 'light' || v === 'dark') return v
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through to system */
  }
  return 'system'
}

/** Current OS/browser dark preference (false when matchMedia is unavailable). */
export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false
}

/** Resolve a choice to a concrete theme given the current OS preference. */
export function resolveTheme(choice: ThemeChoice, prefersDark: boolean): ResolvedTheme {
  if (choice === 'system') return prefersDark ? 'dark' : 'light'
  return choice
}

/** Toggle the `.dark` class on <html> to match a resolved theme. */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

/** The theme actually in effect right now (stored choice resolved against the OS pref). */
export function currentResolvedTheme(): ResolvedTheme {
  return resolveTheme(getStoredChoice(), systemPrefersDark())
}

/**
 * Persist a choice and apply it immediately. 'system' clears the override so the app
 * resumes following the OS preference (and live updates via initTheme's listener).
 */
export function setThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(THEME_STORAGE_KEY)
    else localStorage.setItem(THEME_STORAGE_KEY, choice)
  } catch {
    /* persistence best-effort — still apply below for the current session */
  }
  applyResolvedTheme(resolveTheme(choice, systemPrefersDark()))
}

/**
 * Apply the current theme and subscribe to OS preference changes. The listener only takes
 * effect while the user is in 'system' mode (no explicit override). Returns a cleanup fn.
 */
export function initTheme(): () => void {
  applyResolvedTheme(currentResolvedTheme())
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {}
  }
  const mq = window.matchMedia(DARK_QUERY)
  const onChange = () => {
    if (getStoredChoice() === 'system') {
      applyResolvedTheme(resolveTheme('system', mq.matches))
    }
  }
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}
